import React from 'react';
import { Link } from 'react-router-dom';
import { Search, TrendingUp, Sparkles, ArrowUpRight, ArrowRight } from 'lucide-react';
import useReveal from '../hooks/useReveal';

const PLANS = [
  {
    id: 'foundation',
    icon: 'Search',
    title: 'Foundation',
    price: 'From £595 + VAT / mo',
    description:
      'The automated SEO + GEO growth engine — keyword strategy, up to 30 search-focused articles a month, technical audits and AI-citation monitoring.',
  },
  {
    id: 'growth',
    icon: 'TrendingUp',
    title: 'Growth',
    price: 'From £995 + VAT / mo',
    description:
      'Everything in Foundation, plus a managed programme with monthly human strategy, editorial review, competitor analysis and a strategy call.',
    featured: true,
    badge: 'Recommended',
  },
  {
    id: 'authority',
    icon: 'Sparkles',
    title: 'Authority',
    price: 'From £1,495 + VAT / mo',
    description:
      'For competitive sectors and ecommerce — deeper technical SEO, wider topical authority, custom AI prompt-monitoring and a quarterly roadmap.',
  },
];

const ICONS = { Search, TrendingUp, Sparkles };

function PlanCard({ plan, index }) {
  const ref = useReveal();
  const Icon = ICONS[plan.icon];
  return (
    <article
      ref={ref}
      className={`ai-card reveal ${plan.featured ? 'ai-card-featured' : ''}`}
      style={{ transitionDelay: `${(index % 3) * 90}ms` }}
      data-testid={`home-seo-card-${plan.id}`}
    >
      {plan.badge && <span className="ads-badge">{plan.badge}</span>}
      <div className="ai-card-icon"><Icon size={24} /></div>
      <h3 className="ai-card-title">{plan.title}</h3>
      <span className="ads-price">{plan.price}</span>
      <p className="ai-card-desc">{plan.description}</p>
    </article>
  );
}

export default function SeoTeaser() {
  const headRef = useReveal();
  const ctaRef = useReveal();
  const goContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="section ads-section" id="seo" aria-label="SEO and GEO Growth" data-testid="seo-section">
      <div className="section-inner">
        <div className="section-head reveal" ref={headRef}>
          <span className="section-eyebrow">// ORGANIC GROWTH // SEO &amp; GEO</span>
          <h2 className="section-title">
            GET FOUND ON GOOGLE — AND <span className="text-pink">RECOMMENDED BY AI</span>
          </h2>
          <p className="section-sub">
            Search is changing. Boost!'s SEO + GEO Growth programme combines traditional Search Engine
            Optimisation with Generative Engine Optimisation — so you show up on Google and get
            recommended by ChatGPT, Gemini and Perplexity.
          </p>
        </div>

        <div className="ai-grid ads-grid">
          {PLANS.map((plan, i) => (
            <PlanCard key={plan.id} plan={plan} index={i} />
          ))}
        </div>

        <div className="ai-cta reveal" ref={ctaRef}>
          <p className="ai-cta-text">
            Add it to any website build and we'll waive the setup fee — or run it as a standalone
            monthly programme. Six-month starting commitment recommended.
          </p>
          <div className="ads-cta-btns">
            <Link
              to={{ pathname: '/website-design-pricing', hash: '#seo-geo' }}
              className="boost-btn boost-btn-primary boost-btn-lg"
              data-testid="home-seo-pricing-link"
            >
              See SEO &amp; GEO pricing <ArrowRight size={18} />
            </Link>
            <button className="boost-btn boost-btn-ghost boost-btn-lg" onClick={goContact} data-testid="home-seo-contact">
              Start a project <ArrowUpRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
